// React Imports
import { ReactElement, useEffect, useState } from "react";

// Third Party Libraries Imports
import { Link, useParams } from "react-router-dom";
import moment from "moment";
import TextArea from "antd/es/input/TextArea";
import { useSelector } from "react-redux";
import { Form } from "antd";
import clsx from "clsx";
import { ColumnsType } from "antd/es/table";

// Style Imports
import "./take-withdrawal-action.scss";

// Local Imports
import CustomModal from "../../../../components/custom-modal/custom-modal";
import CollapseComponent from "../../../../components/collapse.component";
import BreadcrumbComponent from "../../../../components/breadcrumb-component/breadcrumb-component";
import WithdrawalService from "../../withdrawal.service";
import TakeActionForm from "../take-action-form/take-action-form";
import Label from "../../../../components/label/label.component";
import CustomInput from "../../../../components/custom-input/custom-input.component";
import {
  extractToEntity,
  formatNumber,
} from "../../../../utils/helper/functions";
import CustomTable from "../../../../components/custom-table/custom-table";
import ReactCountryFlag from "react-country-flag";

const groups: any = {
  OPS: "Operation",
  AMD: "AMD",
  WMD: "WMD",
};

const TakeWithdrawalAction: React.FC = (): ReactElement => {
  const user = useSelector((state: any) => state?.user);
  const [form] = Form.useForm();
  const takeWithdrawalActionService = new WithdrawalService();
  const { withdrawalId }: { [key: string]: string | undefined } = useParams();
  const [openApproveModal, setOpenApproveModal] = useState<boolean>(false);
  const [openRejectModal, setOpenRejectModal] = useState<boolean>(false);
  const [withdrawalDetailsData, setWithdrawalDetailsData] = useState<any>();

  useEffect(() => {
    fetchWithdrawalDetailsData();
  }, []);

  const onApproveWithdrawal = async (values: any) => {
    const response: string | undefined =
      await takeWithdrawalActionService.takeAction(
        withdrawalId,
        "approve",
        values?.statusReason,
        values?.notes,
        values?.transactionId
      );
    if (response === "success") {
      fetchWithdrawalDetailsData();
      setOpenApproveModal(false);
    }
  };

  const onRejectWithdrawal = async (values: any) => {
    const response: string | undefined =
      await takeWithdrawalActionService.takeAction(
        withdrawalId,
        "reject",
        values?.statusReason,
        values?.notes,
        values?.transactionId
      );
    if (response === "success") {
      fetchWithdrawalDetailsData();
      setOpenRejectModal(false);
    }
  };

  const fetchWithdrawalDetailsData = async (): Promise<void> => {
    const data = await takeWithdrawalActionService.getWithdrawDetailsData(
      withdrawalId
    );

    if (data !== undefined) {
      setWithdrawalDetailsData(data);
    }
  };

  const columns: ColumnsType<any> = [
    {
      title: "#",
      dataIndex: "id",
      key: "id",
      fixed: "left",
      render: (_: any, __: any, index: number) => <span>{index + 1}</span>,
    },
    {
      title: "Requested amount",
      dataIndex: "amount",
      key: "amount",
      fixed: "left",
      render: (_: any, record: any) => (
        <span>{formatNumber(record?.convertedAmount)}</span>
      ),
    },
    {
      title: "Currency",
      dataIndex: "currency",
      key: "currency",
      fixed: "left",
      render: (_: any, record: any) => <span>{record?.originalCurrency}</span>,
    },
  ];

  return (
    withdrawalDetailsData && (
      <section className="flex flex-col">
        <BreadcrumbComponent />
        <div className="flex flex-col pb-7 flex-grow">
          <CollapseComponent
            header={
              <div className="flex gap-3 items-center">
                <span className="w-[5px] bg-blue-500 h-[25px] rounded-[10px]"></span>
                <h3 className=" text-xl font-semibold">Portfolio details</h3>
              </div>
            }
          >
            <div className="flex gap-5 flex-wrap mb-5">
              <div className="w-[32%] flex flex-col">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Account#
                </p>
                <Link
                  to={`/home/business-account/${withdrawalDetailsData?.portfolio?.account?.id}/${withdrawalDetailsData?.portfolio?.account?.activeKycId}/details`}
                  className=" text-base font-semibold text-lightGray-200 underline"
                >
                  {withdrawalDetailsData?.portfolio?.account?.accountCode}
                </Link>
              </div>
              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Account type
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {
                    withdrawalDetailsData?.portfolio?.account?.clientType
                      ?.description
                  }{" "}
                  -{" "}
                  {
                    withdrawalDetailsData?.portfolio?.account?.clientSubType
                      ?.description
                  }
                </span>
              </div>
              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Account status
                </p>
                <span className=" text-base font-semibold w-fit px-3 py-1 rounded-md bg-green-100 text-white">
                  {
                    withdrawalDetailsData?.portfolio?.account?.accountStatus
                      ?.description
                  }
                </span>
              </div>

              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Portfolio#
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {withdrawalDetailsData?.portfolio?.accountCode}
                </span>
              </div>
              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Portfolio type
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {withdrawalDetailsData?.portfolio?.managementType}
                </span>
              </div>
              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Portfolio status
                </p>
                <span
                  className={clsx(
                    "text-base font-semibold w-fit px-3 py-1 rounded-md text-white",
                    withdrawalDetailsData?.portfolio?.status === "Opened"
                      ? "bg-green-100"
                      : withdrawalDetailsData?.portfolio?.status === "Rejected"
                      ? "bg-red-200"
                      : "bg-yellow-500"
                  )}
                >
                  {withdrawalDetailsData?.portfolio?.status === "Pending"
                    ? "New"
                    : withdrawalDetailsData?.portfolio?.status}
                </span>
              </div>
              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Portfolio currency
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {withdrawalDetailsData?.portfolio?.portfolioCurrency || "-"}
                </span>
              </div>
              {withdrawalDetailsData?.portfolio?.openDate && (
                <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                  <p className=" text-black-300 text-xs font-normal mb-[10px]">
                    Open date
                  </p>
                  <span className=" text-base font-semibold text-lightGray-200">
                    {moment(withdrawalDetailsData?.portfolio?.openDate).format(
                      "DD MMM, YYYY"
                    )}
                  </span>
                </div>
              )}

              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Wealth manager
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {withdrawalDetailsData?.portfolio?.account?.wealthManager
                    ?.ae1 || "-"}
                </span>
              </div>
              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Wealth manager email
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {withdrawalDetailsData?.portfolio?.account?.wealthManager
                    ?.ae1Email || "-"}
                </span>
              </div>
              {withdrawalDetailsData?.portfolio?.account?.wealthManager
                ?.ae2 && (
                <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                  <p className=" text-black-300 text-xs font-normal mb-[10px]">
                    Wealth manager
                  </p>
                  <span className=" text-base font-semibold text-lightGray-200">
                    {withdrawalDetailsData?.portfolio?.account?.wealthManager
                      ?.ae2 || "-"}
                  </span>
                </div>
              )}
              {withdrawalDetailsData?.portfolio?.account?.wealthManager
                ?.ae2 && (
                <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                  <p className=" text-black-300 text-xs font-normal mb-[10px]">
                    Wealth manager email
                  </p>
                  <span className=" text-base font-semibold text-lightGray-200">
                    {withdrawalDetailsData?.portfolio?.account?.wealthManager
                      ?.ae2Email || "-"}
                  </span>
                </div>
              )}
              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Online trading
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {withdrawalDetailsData?.portfolio?.onlineTradeSys
                    ? "Yes"
                    : "No"}
                </span>
              </div>

              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Pledged
                </p>
                <span
                  className={clsx(
                    "rounded-md w-fit",
                    withdrawalDetailsData?.portfolio?.portfolioDisclaimer
                      ? "bg-[#FFAF00] text-white text-sm font-medium p-2"
                      : "bg-transparent text-lightGray-200 font-semibold text-base p-0"
                  )}
                >
                  {withdrawalDetailsData?.portfolio?.portfolioDisclaimer
                    ? extractToEntity(
                        withdrawalDetailsData?.portfolio?.portfolioDisclaimer
                      )
                    : "No"}
                </span>
              </div>
            </div>
          </CollapseComponent>

          {withdrawalDetailsData?.bankAccount ? (
            <CollapseComponent
              header={
                <div className="flex gap-3 items-center">
                  <span className="w-[5px] bg-blue-500 h-[25px] rounded-[10px]"></span>
                  <h3 className=" text-xl font-semibold">Bank details</h3>
                </div>
              }
            >
              <div className="flex gap-5 flex-wrap mb-5">
                <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                  <p className=" text-black-300 text-xs font-normal mb-[10px]">
                    Country
                  </p>
                  <div className="flex items-center gap-2">
                    <ReactCountryFlag
                      style={{
                        fontSize: "24px",
                      }}
                      countryCode={
                        withdrawalDetailsData?.bankAccount?.bank?.country
                          ?.countryCode
                      }
                      svg
                    />
                    <span className=" text-base font-semibold text-lightGray-200">
                      {withdrawalDetailsData?.bankAccount?.bank?.country
                        ?.nameEn || "-"}
                    </span>
                  </div>
                </div>
                <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                  <p className=" text-black-300 text-xs font-normal mb-[10px]">
                    Bank name
                  </p>
                  <span className=" text-base font-semibold text-lightGray-200">
                    {withdrawalDetailsData?.bankAccount?.bank?.bankName} (
                    {withdrawalDetailsData?.bankAccount?.bank?.refrenceId})
                  </span>
                </div>
                <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                  <p className=" text-black-300 text-xs font-normal mb-[10px]">
                    Currency
                  </p>
                  <div className="flex items-center gap-2">
                    <ReactCountryFlag
                      style={{
                        fontSize: "24px",
                      }}
                      countryCode={
                        withdrawalDetailsData?.bankAccount?.currency
                          ?.countryCode
                      }
                      svg
                    />
                    <span className=" text-base font-semibold text-lightGray-200">
                      {withdrawalDetailsData?.bankAccount?.currency
                        ?.longNameEn || "-"}
                    </span>
                  </div>
                </div>

                {withdrawalDetailsData?.bankAccount?.ibanNumber ? (
                  <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                    <p className=" text-black-300 text-xs font-normal mb-[10px]">
                      IBAN
                    </p>
                    <span className=" text-base font-semibold text-lightGray-200">
                      {withdrawalDetailsData?.bankAccount?.ibanNumber}
                    </span>
                  </div>
                ) : (
                  ""
                )}
                {!withdrawalDetailsData?.bankAccount?.ibanNumber ? (
                  <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                    <p className=" text-black-300 text-xs font-normal mb-[10px]">
                      Account
                    </p>
                    <span className=" text-base font-semibold text-lightGray-200">
                      {withdrawalDetailsData?.bankAccount?.accountNumber}
                    </span>
                  </div>
                ) : (
                  ""
                )}
                {!withdrawalDetailsData?.bankAccount?.ibanNumber ? (
                  <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                    <p className=" text-black-300 text-xs font-normal mb-[10px]">
                      Swift code
                    </p>
                    <span className=" text-base font-semibold text-lightGray-200">
                      {withdrawalDetailsData?.bankAccount?.swiftCode}
                    </span>
                  </div>
                ) : (
                  ""
                )}
                <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                  <p className=" text-black-300 text-xs font-normal mb-[10px]">
                    Additional details
                  </p>
                  <span className=" text-base font-semibold text-lightGray-200">
                    {withdrawalDetailsData?.bankAccount?.additionalInfo || "-"}
                  </span>
                </div>
              </div>
            </CollapseComponent>
          ) : (
            ""
          )}

          <CollapseComponent
            header={
              <div className="flex gap-3 items-center">
                <span className="w-[5px] bg-blue-500 h-[25px] rounded-[10px]"></span>
                <h3 className=" text-xl font-semibold">
                  Withdrawal details{" "}
                  {withdrawalDetailsData?.status === "Approved" ||
                  withdrawalDetailsData?.status === "Rejected" ? (
                    <span
                      className={clsx(
                        "text-base font-semibold w-fit px-3 py-2 ms-3 rounded-md  text-white",
                        withdrawalDetailsData?.status === "Approved"
                          ? "bg-green-100"
                          : "bg-red-200"
                      )}
                    >
                      {withdrawalDetailsData?.status}
                    </span>
                  ) : (
                    ""
                  )}
                </h3>
              </div>
            }
          >
            <div className="flex gap-5 flex-wrap mb-5">
              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Total withdrawal amount
                </p>
                <span className=" text-base font-semibold text-lightGray-200 mb-5">
                  {formatNumber(
                    withdrawalDetailsData?.withdrawAmount,
                    withdrawalDetailsData?.bankAccount?.currency
                      ?.decimalPlace || 2
                  ) +
                    " " +
                    withdrawalDetailsData?.withdrawCurrency}
                </span>
              </div>
              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Currency
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {withdrawalDetailsData?.withdrawCurrency}
                </span>
              </div>
              {withdrawalDetailsData?.fromAvailableCash?.length ? (
                <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                  <div className="w-full">
                    <CustomTable
                      columns={columns}
                      data={withdrawalDetailsData?.fromAvailableCash}
                      showPagination={false}
                    />
                  </div>
                </div>
              ) : (
                ""
              )}
              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  All cash
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {withdrawalDetailsData?.fullAmount ? "Yes" : "No"}
                </span>
              </div>

              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Request date
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {moment(withdrawalDetailsData?.createdAt).format(
                    "DD MMM, YYYY hh:mm A"
                  )}
                </span>
              </div>

              {withdrawalDetailsData?.status !== "Pending" ? (
                <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                  <p className=" text-black-300 text-xs font-normal mb-[10px]">
                    Prospero withdraw transaction ID
                  </p>
                  <span className=" text-base font-semibold text-lightGray-200">
                    {withdrawalDetailsData?.transactionId || "-"}
                  </span>
                </div>
              ) : (
                ""
              )}
              {withdrawalDetailsData?.reviewHistory?.length
                ? withdrawalDetailsData?.reviewHistory?.map(
                    (item: any, index: number) => (
                      <div
                        className="flex w-full gap-5 flex-wrap mb-5"
                        key={index}
                      >
                        <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                          <p className=" text-black-300 text-xs font-normal mb-[10px]">
                            {`${groups[item.group]} team notes`}
                          </p>
                          <span className=" text-base font-semibold text-lightGray-200">
                            {item?.notes || "-"}
                          </span>
                        </div>
                        <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                          <p className=" text-black-300 text-xs font-normal mb-[10px]">
                            {`${groups[item.group]} team action by`}
                          </p>
                          <span className=" text-base font-semibold text-lightGray-200">
                            {item?.actionBy?.name}
                          </span>
                        </div>
                        <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                          <p className=" text-black-300 text-xs font-normal mb-[10px]">
                            {`${groups[item.group]} team action date`}
                          </p>
                          <span className=" text-base font-semibold text-lightGray-200">
                            {moment(item?.actionDate).format(
                              "DD MMM, YYYY hh:mm A"
                            )}
                          </span>
                        </div>
                      </div>
                    )
                  )
                : ""}
            </div>
            {withdrawalDetailsData?.status === "Pending" &&
              withdrawalDetailsData?.approvalStep === user?.withdrawGroup &&
              user?.permissions?.includes("request_withdrawal_approver") && (
                <div className="flex gap-7 justify-end items-center">
                  <button
                    onClick={() => setOpenRejectModal(true)}
                    className="rounded-lg bg-white w-1/2 md:w-fit px-0 md:px-14 py-3 text-center outline-0 cursor-pointer text-xs md:text-sm font-semibold border-[1px] border-blue-500 text-blue-500"
                  >
                    Reject
                  </button>
                  <button
                    onClick={() => setOpenApproveModal(true)}
                    className="rounded-lg bg-blue-500 w-1/2 md:w-fit px-0 md:px-14 py-3 text-center outline-0 cursor-pointer text-xs md:text-sm font-semibold border-[1px] border-blue-500 text-white"
                  >
                    Approve withdrawal
                  </button>
                </div>
              )}
          </CollapseComponent>
        </div>

        <CustomModal
          isVisible={openApproveModal}
          centered
          width={450}
          onClose={() => {
            setOpenApproveModal(false);
          }}
        >
          <TakeActionForm>
            <Form form={form} onFinish={onApproveWithdrawal} scrollToFirstError>
              {user?.withdrawGroup === "OPS" && (
                <div className="fields flex flex-col">
                  <Label text="Prospero withdraw transaction ID" />
                  <Form.Item
                    name="transactionId"
                    rules={[
                      {
                        required: true,
                        message: `Please input withdraw transaction ID!`,
                      },
                    ]}
                  >
                    <CustomInput
                      placeholder={`Enter withdraw transaction ID`}
                      type="number"
                    />
                  </Form.Item>
                </div>
              )}
              <div className="fields flex flex-col">
                <Label
                  text={
                    user?.withdrawGroup === "AMD"
                      ? "AMD team notes"
                      : user?.withdrawGroup === "WMD"
                      ? "WMD team notes"
                      : "Operation team notes"
                  }
                />
                <Form.Item
                  name="notes"
                  rules={[
                    {
                      required: true,
                      message: `Please input notes!`,
                    },
                  ]}
                >
                  <TextArea rows={4} placeholder={`Enter notes`} />
                </Form.Item>
              </div>
              <div className="buttons-container flex align-center justify-space-between">
                <button
                  className="cancel"
                  type="button"
                  onClick={() => setOpenApproveModal(false)}
                >
                  Cancel
                </button>
                <button>Submit</button>
              </div>
            </Form>
          </TakeActionForm>
        </CustomModal>

        <CustomModal
          isVisible={openRejectModal}
          centered
          width={450}
          onClose={() => {
            setOpenRejectModal(false);
          }}
        >
          <TakeActionForm>
            <Form
              form={form}
              onFinish={onRejectWithdrawal}
              scrollToFirstError={true}
            >
              <div className="fields flex flex-col">
                <Label text="Rejection reason" />
                <Form.Item
                  name="notes"
                  rules={[
                    {
                      required: true,
                      message: `Please input Reason!`,
                    },
                  ]}
                >
                  <TextArea rows={4} placeholder={`Enter Reason`} />
                </Form.Item>
              </div>
              <div className="buttons-container flex align-center justify-space-between">
                <button
                  className="cancel"
                  type="button"
                  onClick={() => setOpenRejectModal(false)}
                >
                  Cancel
                </button>
                <button>Submit</button>
              </div>
            </Form>
          </TakeActionForm>
        </CustomModal>
      </section>
    )
  );
};

export default TakeWithdrawalAction;
